import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchPaises, deletePais } from '../actions/paisActions';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { toast } from 'react-toastify';
import { Pagination, Table, Modal, Button } from 'react-bootstrap';
import PaisForm from './PaisForm';
import CustomPrintModal from './CustomPrintModal';
import './PaisList.css';

const PaisList = () => {
    const navigate = useNavigate();
    const location = useLocation();
    useEffect(() => {
        const fromHeader = location.state?.fromHeader;
        if (!fromHeader) {
            navigate('/'); // Redirige a la página principal si no es llamado desde Header
        }
    }, [navigate]);
    const dispatch = useDispatch();
    const { paises, error, loading } = useSelector(state => state.pais);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [showModal, setShowModal] = useState(false);
    const [selectedPais, setSelectedPais] = useState(null);
    const [showPrintModal, setShowPrintModal] = useState(false);
    const [content, setContent] = useState({ head: [], body: [] });

    useEffect(() => {
        dispatch(fetchPaises()).catch(error => {
            toast.error(`Error fetching countries: ${error}`);
        });
    }, [dispatch]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentPaises = paises.slice(indexOfFirstItem, indexOfLastItem);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);
    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(parseInt(event.target.value, 10));
        setCurrentPage(1);
    };
    const totalPages = Math.ceil(paises.length / itemsPerPage);

    const handleDelete = (id) => {
        confirmAlert({
            title: 'Confirmar eliminación',
            message: '¿Estás seguro de que deseas eliminar el país seleccionado?',
            buttons: [
                {
                    label: 'Sí',
                    onClick: () => {
                        dispatch(deletePais(id))
                            .then(() => {
                                toast.success('País eliminado correctamente');
                                dispatch(fetchPaises());
                            })
                            .catch(error => {
                                const errorMessage = error.response ? error.response.data.message : error.message;
                                toast.error(`Error al eliminar el país: ${errorMessage}`);
                            });
                    }
                },
                {
                    label: 'No'
                }
            ]
        });
    };

    const handlePrintModalShow = () => {
        if (paises.length === 0) {
            toast.error('No hay países para imprimir');
            return;
        }
        const content = {
            head: [["N°", "Nombre del País"]],
            body: paises.map((pais, index) => [index+1, pais.nombre])
        };
        setContent(content);
        setShowPrintModal(true);
    };

    const handleModalClose = () => setShowModal(false);

    const handleModalShow = (pais) => {
        setSelectedPais(pais);
        setShowModal(true);
    };

    const handlePrintModalClose = () => {
        setShowPrintModal(false);
    };

    const handleSave = () => {
        dispatch(fetchPaises()).catch(error => {
            toast.error(`Error fetching countries: ${error}`);
        });
    };

    if (loading) return <p>Cargando países...</p>;
    if (error) return <p>Error al cargar los datos: {error}</p>;

    return (
        <div className="list4-container container-list">
            <h2>Listado de Países</h2>
            <div className="mt-3">
                <button onClick={() => handleModalShow(null)} className="button btn-accent float-end">
                    <i className="bi bi-plus-lg"></i> Nuevo País
                </button>
                <label htmlFor="items-per-page">Registros por página:</label>
                <select id="items-per-page" value={itemsPerPage} onChange={handleItemsPerPageChange}>
                    <option value="10">10</option>
                    <option value="15">15</option>
                    <option value="20">20</option>
                    <option value="30">30</option>
                </select>
            </div>
            <div className='table-container'>
                <Table hover striped border={1}>
                    <thead>
                        <tr>
                            <th className='th-cod'>N°</th>
                            <th>Nombre</th>
                            <th className='th-actions'>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentPaises.map((pais, index) => (
                            <tr key={pais.id}>
                                 <td className='td-cod'>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                 <td>{pais.nombre}</td>
                                <td>
                                    <button onClick={() => handleModalShow(pais)} className="button btn-accent-outline"><i className="bi bi-pencil"></i></button>
                                    <button onClick={() => handleDelete(pais.id)} className="button btn-accent-outline"><i className="bi bi-trash"></i></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
            <div className='text-center mb-3'>
                <button onClick={() => navigate('/')} className="button btn-cancel" style={{ marginRight: '10px' }}> <i className="bi bi-arrow-left"></i> Volver</button>
                <button onClick={handlePrintModalShow} className="button btn-accent"><i className="bi bi-printer"></i> Imprimir</button>
            </div>
            <div className="pagination-container">
                <Pagination className="custom-pagination">
                    <Pagination.First
                        onClick={() => paginate(1)}
                        disabled={currentPage === 1}
                    />
                    <Pagination.Prev
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                    />
                    {[...Array(totalPages)].map((_, index) => (
                        <Pagination.Item
                            key={index + 1}
                            active={index + 1 === currentPage}
                            onClick={() => paginate(index + 1)}
                        >
                            {index + 1}
                        </Pagination.Item>
                    ))}
                    <Pagination.Next
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    />
                    <Pagination.Last
                        onClick={() => paginate(totalPages)}
                        disabled={currentPage === totalPages}
                    />
                </Pagination>
            </div>
            
            <Modal show={showModal} onHide={handleModalClose}>
                <Modal.Header closeButton>
                    <Modal.Title>{selectedPais ? 'Editar País' : 'Nuevo País'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <PaisForm pais={selectedPais} onClose={handleModalClose} onSave={handleSave} />
                </Modal.Body>
            </Modal>

            <CustomPrintModal show={showPrintModal} handleClose={handlePrintModalClose} title="LISTA GENERAL DE PAISES" content={content} />
        </div>
    );
};

export default PaisList;

